import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  ShieldCheck, Loader2, RefreshCw, Download, Activity, MapPin, AlertTriangle,
  CheckCircle2, Clock, Users, Building2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ADMIN_NAV = [
  { icon: Activity, label: 'Operations Executive', href: '/operations/executive' },
  { icon: Building2, label: 'Referral Sources', href: '/operations/referral-sources' },
  { icon: ShieldCheck, label: 'EVV Compliance', active: true },
];

const FORMAT_LABEL = {
  carelynk_json: 'CaraLoom JSON (canonical)',
  hhaexchange: 'HHAeXchange',
  sandata: 'Sandata',
  tellus: 'Tellus',
};

export default function AdminEvvAudit() {
  const { getAuthHeader } = useAuth();
  const [days, setDays] = useState(30);
  const [compliance, setCompliance] = useState(null);
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportingFormat, setExportingFormat] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, v] = await Promise.all([
        axios.get(`${API}/admin/evv/compliance`, { ...getAuthHeader(), params: { days } }),
        axios.get(`${API}/admin/evv/visits`, { ...getAuthHeader(), params: { days, limit: 200 } }),
      ]);
      setCompliance(c.data);
      setVisits(v.data.visits || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load EVV compliance');
    } finally {
      setLoading(false);
    }
  }, [days, getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const doExport = async (fmt) => {
    setExportingFormat(fmt);
    try {
      const { data } = await axios.post(`${API}/admin/evv/export`, {
        format: fmt, window_days: days, only_completed: true,
      }, getAuthHeader());
      // Download as a JSON file
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `evv_${fmt}_${days}d_${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${data.count} visits exported in ${FORMAT_LABEL[fmt]} format`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Export failed');
    } finally {
      setExportingFormat(null);
    }
  };

  const targetPct = compliance?.compliance_target_pct ?? 95;
  const compliancePct = compliance?.compliance_pct ?? 0;
  const trafficLight = useMemo(() => {
    if (compliancePct >= targetPct) return { tone: 'emerald', label: 'On target' };
    if (compliancePct >= targetPct * 0.85) return { tone: 'amber', label: 'Close to target' };
    return { tone: 'red', label: 'Below target' };
  }, [compliancePct, targetPct]);

  return (
    <DashboardShell
      navItems={ADMIN_NAV}
      title="EVV Compliance — Electronic Visit Verification"
      subtitle="Federal 21st Century Cures Act requires every Medicaid PCS / HHCS visit be GPS-verified. This is your audit-ready evidence trail."
      testId="admin-evv-page"
    >
      {/* CONTROLS */}
      <div className="cn-card p-4 mb-6 flex flex-wrap items-center gap-3" data-testid="evv-controls">
        <div className="flex items-center gap-2">
          <span className="text-xs text-cn-text-muted font-semibold">Window</span>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="border border-cn-border rounded-lg px-2 py-1.5 text-sm bg-white" data-testid="evv-window-select">
            {[7, 30, 90, 180, 365].map((d) => <option key={d} value={d}>Last {d} days</option>)}
          </select>
        </div>
        <Button onClick={load} disabled={loading} className="cn-btn cn-btn-ghost !py-2" data-testid="evv-refresh">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
        <div className="ml-auto flex flex-wrap gap-2" data-testid="evv-export-row">
          {Object.entries(FORMAT_LABEL).map(([fmt, lbl]) => (
            <Button
              key={fmt}
              onClick={() => doExport(fmt)}
              disabled={exportingFormat === fmt}
              className="cn-btn cn-btn-ghost !py-2 text-xs"
              data-testid={`evv-export-${fmt}`}
            >
              {exportingFormat === fmt ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
              {lbl}
            </Button>
          ))}
        </div>
      </div>

      {loading && <div className="cn-card p-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-cn-primary mx-auto" /></div>}

      {!loading && compliance && (
        <>
          {/* HEADLINE KPI */}
          <div className="cn-card p-6 mb-6" data-testid="evv-headline">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">EVV Compliance · last {days} days</p>
                <p className="font-display font-bold text-5xl text-cn-text dark:text-white mt-2">{compliance.compliance_pct.toFixed(1)}%</p>
                <p className="text-sm text-cn-text-muted mt-1">
                  {compliance.evv_matched} / {compliance.total_completed_appointments} completed visits captured
                  · target {compliance.compliance_target_pct}%
                </p>
              </div>
              <div className="flex flex-col items-end gap-2">
                <span className={`text-xs px-3 py-1.5 rounded-full font-semibold border ${
                  trafficLight.tone === 'emerald' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                  trafficLight.tone === 'amber' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                  'bg-red-50 text-red-700 border-red-200'
                }`} data-testid="evv-traffic-light">
                  {trafficLight.label}
                </span>
                <span className="text-xs text-cn-text-muted">{compliance.evv_missing} visits without EVV</span>
              </div>
            </div>

            {/* Progress bar */}
            <div className="mt-5">
              <div className="h-3 bg-cn-bone-100 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all ${
                    trafficLight.tone === 'emerald' ? 'bg-emerald-500' :
                    trafficLight.tone === 'amber' ? 'bg-amber-500' : 'bg-red-500'
                  }`}
                  style={{ width: `${Math.min(100, compliancePct)}%` }}
                />
              </div>
              <div className="flex justify-between text-[10px] text-cn-text-muted mt-1">
                <span>0%</span>
                <span>target {compliance.compliance_target_pct}%</span>
                <span>100%</span>
              </div>
            </div>
          </div>

          {/* DETAIL TILES */}
          <div className="grid sm:grid-cols-3 gap-4 mb-6" data-testid="evv-detail-tiles">
            <DetailTile
              label="GPS-verified" icon={MapPin}
              value={`${compliance.gps_verified}`}
              sub={`${compliance.gps_pct.toFixed(1)}% of captured visits use GPS`}
              testid="evv-tile-gps"
            />
            <DetailTile
              label="Missing EVV" icon={AlertTriangle}
              value={`${compliance.evv_missing}`}
              sub="Visits with no electronic verification"
              testid="evv-tile-missing"
              tone={compliance.evv_missing > 0 ? 'amber' : 'emerald'}
            />
            <DetailTile
              label="Clinicians lagging" icon={Users}
              value={`${compliance.laggards?.length || 0}`}
              sub="Below 95% compliance · 3+ visits"
              testid="evv-tile-laggards"
              tone={(compliance.laggards?.length || 0) > 0 ? 'amber' : 'emerald'}
            />
          </div>

          {/* LAGGARDS */}
          {(compliance.laggards || []).length > 0 && (
            <div className="cn-card p-5 mb-6" data-testid="evv-laggards-card">
              <h4 className="font-display font-semibold text-base text-cn-text mb-3">Clinicians below target ({compliance.laggards.length})</h4>
              <div className="space-y-2">
                {compliance.laggards.map((l) => (
                  <div key={l.clinician_id} className="flex items-center justify-between text-sm py-2 border-b border-cn-border last:border-0" data-testid={`evv-laggard-${l.clinician_id}`}>
                    <div>
                      <p className="font-semibold text-cn-text">{l.clinician_id}</p>
                      <p className="text-xs text-cn-text-muted">{l.with_evv} / {l.total} visits verified</p>
                    </div>
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-semibold">{l.compliance_pct.toFixed(1)}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* RECENT VISITS TABLE */}
          <div className="cn-card overflow-hidden" data-testid="evv-visits-table-card">
            <div className="p-5 border-b border-cn-border">
              <h4 className="font-display font-semibold text-base text-cn-text">Recent EVV records ({visits.length})</h4>
              <p className="text-xs text-cn-text-muted mt-0.5">Most recent first. Use the export buttons above to ship a state-aggregator batch.</p>
            </div>
            {visits.length === 0 ? (
              <div className="p-10 text-center" data-testid="evv-visits-empty">
                <ShieldCheck className="w-6 h-6 text-cn-text-muted mx-auto mb-2" />
                <p className="text-sm text-cn-text-muted">No EVV records in this window yet.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="evv-visits-table">
                  <thead className="bg-cn-bone-50 border-b border-cn-border text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">
                    <tr className="text-left">
                      <th className="px-5 py-3">Clinician</th>
                      <th className="px-3 py-3">Service</th>
                      <th className="px-3 py-3">Started</th>
                      <th className="px-3 py-3 text-right">Duration</th>
                      <th className="px-3 py-3">Method</th>
                      <th className="px-3 py-3">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-cn-border">
                    {visits.map((v) => (
                      <tr key={v.id} className="hover:bg-cn-bone-50/40" data-testid={`evv-row-${v.id}`}>
                        <td className="px-5 py-2.5">
                          <p className="font-semibold text-cn-text">{v.clinician_name || v.clinician_id}</p>
                          <p className="text-[10px] text-cn-text-muted">Appt: {v.appointment_id?.slice(0, 8)}…</p>
                        </td>
                        <td className="px-3 py-2.5">{v.service_type || '—'}</td>
                        <td className="px-3 py-2.5">{v.started_at ? new Date(v.started_at).toLocaleString() : '—'}</td>
                        <td className="px-3 py-2.5 text-right font-medium">{v.duration_minutes !== undefined ? `${v.duration_minutes} min` : '—'}</td>
                        <td className="px-3 py-2.5">
                          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-cn-bone-100 border border-cn-border text-cn-text-muted">{v.verification_method}</span>
                        </td>
                        <td className="px-3 py-2.5">
                          <StatusPill status={v.status} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </DashboardShell>
  );
}

function DetailTile({ label, value, sub, icon: Icon, tone = 'neutral', testid }) {
  const ring = {
    neutral: 'text-cn-primary',
    amber: 'text-amber-600',
    emerald: 'text-emerald-600',
  }[tone];
  return (
    <div className="cn-card p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">{label}</span>
        <Icon className={`w-4 h-4 ${ring}`} />
      </div>
      <p className="font-display font-bold text-3xl text-cn-text dark:text-white">{value}</p>
      <p className="text-xs text-cn-text-muted mt-1">{sub}</p>
    </div>
  );
}

function StatusPill({ status }) {
  const cls = {
    completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    in_progress: 'bg-amber-50 text-amber-700 border-amber-200',
    missed: 'bg-red-50 text-red-700 border-red-200',
    manual_corrected: 'bg-cn-bone-100 text-cn-text border-cn-border',
  }[status] || 'bg-cn-bone-100 text-cn-text border-cn-border';
  const Icon = status === 'completed' ? CheckCircle2 :
    status === 'in_progress' ? Clock : AlertTriangle;
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold inline-flex items-center gap-1 ${cls}`}>
      <Icon className="w-3 h-3" /> {status}
    </span>
  );
}
